import { tw } from "@careinfo/tailwind";
import React from "react";
import { formatAmount } from "../../../utils/formatAmount";

interface AccountNode {
  name: string;
  opening_debit?: number;
  opening_credit?: number;
  period_debit?: number;
  period_credit?: number;
  closing_balance?: number;
  children?: AccountNode[];
}

interface TrialData {
  accounts: AccountNode[];
}

const TrialGroupedTable = ({ data }: { data: TrialData }) => {
  let grandOpeningDebit = 0;
  let grandOpeningCredit = 0;
  let grandClosingDebit = 0;
  let grandClosingCredit = 0;
  let grandPeriodicalDebit = 0;
  let grandPeriodicalCredit = 0;

  const traverse = (nodes?: AccountNode[]) => {
    if (!nodes) return;
    for (const node of nodes) {
      const isLeaf = !node.children || node.children.length === 0;

      if (isLeaf) {
        // --- Opening Totals ---
        if (node.opening_debit) {
          grandOpeningDebit += node.opening_debit;
        }
        if (node.opening_credit) {
          grandOpeningCredit += node.opening_credit;
        }

        // --- Closing Totals ---
        if (node.closing_balance !== undefined) {
          if (node.closing_balance > 0) {
            grandClosingCredit += node.closing_balance;
          } else if (node.closing_balance < 0) {
            grandClosingDebit += Math.abs(node.closing_balance);
          }
        }

        // --- Periodical Totals ---
        if (node.period_debit) {
          grandPeriodicalDebit += node.period_debit;
        }
        if (node.period_credit) {
          grandPeriodicalCredit += node.period_credit;
        }
      }
      traverse(node.children);
    }
  };

  traverse(data.accounts);

  return (
    <div
      className={tw`overflow-x-auto bg-[#fefefe] rounded-md border border-[#dcdcdc] shadow`}
    >
      <table className={tw`w-full rounded-md overflow-hidden border-collapse`}>
        <thead>
          <tr className={tw`text-white bg-primary`}>
            <th rowSpan={2} className={tw`px-4 py-2 text-left`}>
              Account Name
            </th>
            <th colSpan={2} className={tw`px-4 py-2 text-center`}>
              Opening
            </th>
            <th colSpan={2} className={tw`px-4 py-2 text-center`}>
              Periodical
            </th>
            <th colSpan={2} className={tw`px-4 py-2 text-center`}>
              Closing
            </th>
          </tr>
          <tr className={tw`text-white bg-primary`}>
            <th className={tw`px-4 py-2 text-center`}>Debit</th>
            <th className={tw`px-4 py-2 text-center`}>Credit</th>
            <th className={tw`px-4 py-2 text-center`}>Debit</th>
            <th className={tw`px-4 py-2 text-center`}>Credit</th>
            <th className={tw`px-4 py-2 text-center`}>Debit</th>
            <th className={tw`px-4 py-2 text-center`}>Credit</th>
          </tr>
        </thead>
        <tbody>
          {data.accounts.map((group) => (
            <React.Fragment key={group.name}>
              <tr className={tw`border-b`}>
                <td
                  colSpan={7}
                  className={tw`px-4 py-2 font-semibold text-primary uppercase`}
                >
                  {group.name}
                </td>
              </tr>

              {group.children?.map((level2) => (
                <React.Fragment key={level2.name}>
                  {level2.children?.map((level3) => (
                    <tr key={level3.name} className={tw`border-b text-center`}>
                      <td className={tw`px-10 py-2 text-left`}>
                        {level3.name}
                      </td>
                      <td className={tw`px-4 py-2`}>
                        {formatAmount(level3.opening_debit)}
                      </td>
                      <td className={tw`px-4 py-2`}>
                        {formatAmount(level3.opening_credit)}
                      </td>
                      <td className={tw`px-4 py-2`}>
                        {formatAmount(level3.period_debit)}
                      </td>
                      <td className={tw`px-4 py-2`}>
                        {formatAmount(level3.period_credit)}
                      </td>
                      <td className={tw`px-4 py-2`}>
                        {level3.closing_balance !== undefined &&
                        level3.closing_balance < 0
                          ? formatAmount(Math.abs(level3.closing_balance))
                          : "0.00"}
                      </td>
                      <td className={tw`px-4 py-2`}>
                        {level3.closing_balance !== undefined &&
                        level3.closing_balance > 0
                          ? formatAmount(level3.closing_balance)
                          : "0.00"}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}

          <tr className={tw`bg-gray-200 font-bold text-center`}>
            <td className={tw`px-4 py-2 text-left`}> TOTAL </td>
            <td className={tw`px-4 py-2`}>{formatAmount(grandOpeningDebit)}</td>
            <td className={tw`px-4 py-2`}>{formatAmount(grandOpeningDebit)}</td>
            <td className={tw`px-4 py-2`}>
              {formatAmount(grandPeriodicalDebit)}
            </td>
            <td className={tw`px-4 py-2`}>
              {formatAmount(grandPeriodicalCredit)}
            </td>
            <td className={tw`px-4 py-2`}>{formatAmount(grandClosingDebit)}</td>
            <td className={tw`px-4 py-2`}>
              {formatAmount(grandClosingCredit)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default TrialGroupedTable;
